import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownButtonComponent, type ItemModel } from '@syncfusion/ej2-react-splitbuttons';
import { useLogout } from '../hooks/useLogout';
import type { Perspective } from '../../constants';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import ChangePasswordDialog from './ChangePasswordDialog';
import { useEffect, useState, useRef } from 'react';
import { useStore } from '../../store';
import { FormDialog } from './FormDialog';
import { api } from '../api/api';
import { APP_VERSION, BUILD_COMMIT, BUILD_TIME, formatBuild } from '../utils/buildInfo';


export default function Header({ perspectives, currentPerspective, onPerspectiveChange, sidebarOpen, setSidebarOpen, showSidebarToggle = true }: { perspectives: Perspective[]; currentPerspective: string; onPerspectiveChange: any, sidebarOpen: boolean; setSidebarOpen: (open: boolean) => void, showSidebarToggle?: boolean }) {
    const { t, i18n } = useTranslation(['common']);

    const userMenuItems: ItemModel[] = [
        { id: 'account', text: t('user.account', { ns: 'common' }) },
        { id: 'change_password', text: t('auth.change_password', { ns: 'common' }) },
        { id: 'about_planback', text: t('user.about_planback', { ns: 'common' }) },
        { id: 'logout', text: t('actions.logout', { ns: 'common' }) },
    ];

    const logout = useLogout();
    
    const [showAccountModal, setShowAccountModal] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [showAboutModal, setShowAboutModal] = useState(false);
    const [backendVersion, setBackendVersion] = useState<string | null>(null);
    const [schedulingVersion, setSchedulingVersion] = useState<string | null>(null);
    const [backendBuildInfo, setBackendBuildInfo] = useState<{ commit?: string; time?: string } | null>(null);
    const frontendBuild = formatBuild(BUILD_COMMIT, BUILD_TIME, i18n.language);
    const backendBuild = formatBuild(backendBuildInfo?.commit, backendBuildInfo?.time, i18n.language);
    const [tenantLogoUrl, setTenantLogoUrl] = useState<string | null>(null);
    const tenantLogoUrlRef = useRef<string | null>(null);
    const userProfile = useStore((s) => s.userProfile);

    useEffect(() => {
        let cancelled = false;
        api.get('/api/tenants/current/logo', { responseType: 'blob' })
            .then((res) => {
                if (!cancelled && res.data) {
                    const url = URL.createObjectURL(res.data as Blob);
                    if (tenantLogoUrlRef.current) URL.revokeObjectURL(tenantLogoUrlRef.current);
                    tenantLogoUrlRef.current = url;
                    setTenantLogoUrl(url);
                }
            })
            .catch(() => {
                if (!cancelled) setTenantLogoUrl(null);
            });
        return () => {
            cancelled = true;
            if (tenantLogoUrlRef.current) {
                URL.revokeObjectURL(tenantLogoUrlRef.current);
                tenantLogoUrlRef.current = null;
            }
            setTenantLogoUrl(null);
        };
    }, []);
    
    const handleUserMenuClick = async (args: any) => {
        if (args.item.id === 'logout') {
            await logout();
        } else if (args.item.id === 'account') {
            setShowAccountModal(true);
        } else if (args.item.id === 'change_password') {
            setShowChangePasswordModal(true);
        } else if (args.item.id === 'about_planback') {
            api.get<{ backendVersion: string; schedulingVersion: string; buildCommit?: string; buildTime?: string }>('/api/version')
                .then((res) => {
                    setBackendVersion(res.data.backendVersion);
                    setSchedulingVersion(res.data.schedulingVersion);
                    setBackendBuildInfo({ commit: res.data.buildCommit, time: res.data.buildTime });
                })
                .catch(() => {
                    setBackendVersion(null);
                    setSchedulingVersion(null);
                    setBackendBuildInfo(null);
                });
            setShowAboutModal(true);
        }
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    // Helper to get initials from username
    const getInitials = (name: string) => {
      if (!name) return '';
      const parts = name.split(/\s|\./).filter(Boolean);
      if (parts.length === 1) return parts[0][0].toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    return <header className="h-[85px] w-full flex items-center justify-between px-5 border-b shadow-sm bg-white z-10">
            {showSidebarToggle && (
              <ButtonComponent
                  iconCss="e-icons e-menu"
                  cssClass="e-flat"
                  onClick={toggleSidebar}
              />
            )}
            {!showSidebarToggle && <div className="w-10" />} {/* Spacer when toggle is hidden */}
            {/* Left: PlanBack Logo + optional Customer Logo */}
            <div className="flex items-center gap-8 px-4">
              <img src="/PlanBack_r50.png" alt="PlanBack" className="h-12 w-auto" />
              {tenantLogoUrl && (
                <img src={tenantLogoUrl} alt="Kundenlogo" className="h-10 w-auto max-h-12 object-contain" />
              )}
            </div>
    
            {/* Center: Perspective Buttons */}
            <div className="flex space-x-2 ml-auto mr-4">
              {perspectives.map((p) => (
                <ButtonComponent
                  key={p.key}
                  cssClass={`e-large ${currentPerspective === p.key ? 'e-primary' : 'e-flat text-gray-600'}`}
                  onClick={() => onPerspectiveChange(p.key)}
                >
                  {t(p.labelKey)}
                </ButtonComponent>
              ))}
            </div>

            {/* Right: Language and User Menu */}
            <div className="flex items-center gap-2">
              <div data-testid="user-menu">
                <DropDownButtonComponent
                  items={userMenuItems}
                  select={handleUserMenuClick}
                  cssClass="e-flat bg-transparent border-none shadow-none e-caret-hide"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-lg font-semibold text-white">
                    {getInitials(userProfile?.username || '')}
                  </div>
                </DropDownButtonComponent>
              </div>
              {showAccountModal && (
                <FormDialog
                  value={userProfile}
                  onClose={() => setShowAccountModal(false)}
                  onSave={(_data: any) => {
                    // TODO: Implement save logic (API call)
                    setShowAccountModal(false);
                  }}
                  titleKey="user.account"
                >
                  {({ register, formState }: any) => (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                          {t('auth.username')} *
                        </label>
                        <input
                          type="text"
                          className="e-input w-full bg-gray-100 cursor-not-allowed select-none"
                          value={userProfile?.username || ''}
                          readOnly
                          required
                          aria-required="true"
                          tabIndex={-1}
                          style={{ pointerEvents: 'none' }}
                        />
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">{t('user.firstName', 'First Name')}</label>
                        <input type="text" className="e-input w-full" {...register('firstName')} />
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">{t('user.familyName', 'Family Name')}</label>
                        <input type="text" className="e-input w-full" {...register('familyName')} />
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">{t('auth.email', 'E-Mail')}</label>
                        <input type="email" className="e-input w-full" {...register('email', { pattern: /^[^@\s]+@[^@\s]+\.[^@\s]+$/ })} />
                        {formState.errors.email && (
                          <div className="text-red-500 text-sm mt-1">{t('auth.email_invalid', 'Invalid email address')}</div>
                        )}
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('language', 'Language')}</label>
                        <LanguageSwitcher />
                      </div>
                    </>
                  )}
                </FormDialog>
              )}
              {showChangePasswordModal && (
                <ChangePasswordDialog onClose={() => setShowChangePasswordModal(false)} />
              )}
              {showAboutModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowAboutModal(false)}>
                  <div className="bg-white rounded shadow-lg p-6 min-w-[300px]" onClick={(e) => e.stopPropagation()}>
                    <h2 className="text-lg font-semibold mb-4">{t('mobile.about.title')}</h2>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">{t('mobile.about.frontend')}:</span> v{APP_VERSION}
                        {frontendBuild && <span className="text-gray-500"> ({t('mobile.about.build')} {frontendBuild})</span>}
                      </div>
                      <div>
                        <span className="font-medium">{t('mobile.about.backend')}:</span> v{backendVersion ?? t('mobile.about.unknown')}
                        {backendBuild && <span className="text-gray-500"> ({t('mobile.about.build')} {backendBuild})</span>}
                      </div>
                      <div><span className="font-medium">{t('mobile.about.scheduling')}:</span> v{schedulingVersion ?? t('mobile.about.unknown')}</div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <ButtonComponent cssClass="e-flat" onClick={() => setShowAboutModal(false)}>
                        {t('actions.close')}
                      </ButtonComponent>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </header>;
}